import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppSelector } from '../../../../hooks/react-redux';
import { getAllRoles, getFetchingRoles } from '../../../admin/roles/store';
import { ClientUser, getAllUsers, getIsFetchingUsers } from '../../../admin/users/store';
import { roleLabels } from '../../../constants/roleLabels';
import { PieChartAnalyticsData } from '../../../shared/analytics/PieChart';
import { User } from '../../../shared/icons';
import { ChartLoading } from '../ChartLoading';
import { PieChartWrapper } from '../PieChartWrapper';

interface UsersChartProps {
    dimensions?: {
        height: number;
        width: number;
    }
    analyticsSpinner: boolean;
}

export const UsersChart: React.FC<UsersChartProps> = ({ dimensions, analyticsSpinner }) => {
    const allUsers = useAppSelector(getAllUsers);
    const allRoles = useAppSelector(getAllRoles);
    const isFetchingRoles = useAppSelector(getFetchingRoles);
    const isFetchingUsers = useAppSelector(getIsFetchingUsers);
    const showSpinner = useMemo(() => isFetchingUsers || isFetchingRoles || analyticsSpinner, [isFetchingUsers, isFetchingRoles, analyticsSpinner]);

    const getRoleName = useCallback((user: ClientUser) => {
        if (!isNull(user.roleId)) {
            const foundRole = allRoles.find(({ roleId }) => user.roleId === roleId!);
            if (foundRole) {
                return foundRole.roleName;
            }
        }
        return roleLabels[user.role];
    }, [allRoles]);

    const data = useMemo(() => allUsers.reduce((acc: PieChartAnalyticsData[], user) => {
        const roleName = getRoleName(user);
        const currentCount = acc.find(({ key }) => key === roleName)?.value || 0;
        const updatedRoleDataCount = { key: roleName, value: currentCount + 1 };
        return [...acc.filter(({ key }) => key !== roleName), updatedRoleDataCount];
    }, []).sort((a, b) => a.value - b.value), [allUsers, getRoleName]);

    if (showSpinner) {
        return <ChartLoading dimensions={dimensions} />;
    }

    return (
        <PieChartWrapper
            data={data}
            dimensions={dimensions}
            icon={User}
            testId='homepage-user-roles'
            label='User Roles'
            colors='amethystFrench'
        />
    );
};
